/**
 * What a PERSON may call, by role — the one table, shared by the API gate and the console.
 *
 * The session carries the `app_role` of the person's platform-hub grant on Rathvan ID
 * (VIEWER < MEMBER < ADMIN < SUPER_ADMIN), and this is the whole meaning of it:
 *
 *   VIEWER       read-only: every GET.
 *   MEMBER       may also write CAPABILITY DATA — what the tenant's services produce and the hub
 *                records: signals, log lines, health checks, alerts, test runs, and a model-router
 *                completion (usage of the tenant's provider key, not a change to it).
 *   ADMIN        may also change CONFIGURATION — what shapes the tenant: service targets, alert
 *                routes, gateway routes, traffic splits, machine keys (mint / disable / monthly
 *                token cap), the model router's provider keys (add / disable), and the tenant's
 *                observability backend (its Grafana token, datasource and URL).
 *   SUPER_ADMIN  everything ADMIN may; reserved by the issuer, not distinguished here yet.
 *
 * It lives in `@plat/core` rather than in the API because TWO readers need the same answer: the
 * gate in `apps/api/src/server.ts`, which refuses a write below the minimum with a 403 before any
 * handler runs, and the console, which does not render a form the session's role could never
 * submit. The console's `mayWrite` is derived from this table, never from a second list of paths —
 * a copy would agree on the day it was written and silently diverge on the day a row changed. The
 * server remains the authority: hiding a form is a courtesy, the 403 is the control, and
 * `roles.test.ts` in the API proves the 403 for every write route whatever the console shows.
 *
 * One table, first match wins. A prefix ending in `/` matches everything under it; any other entry
 * matches the path itself and anything under it (`/v1/api-keys` covers `/v1/api-keys/:id/disable`).
 * The LAST row is the fail-closed default: a write route nobody listed is ADMIN-only until a line
 * here says otherwise, so a new capability's POST cannot land as MEMBER-writable by omission. A
 * session with no role (a cookie set before roles were enforced) or with a role outside ROLES is
 * VIEWER — the lowest, never a guess upward — and so is a dev caller that sends no
 * `x-plat-dev-role`. Mirrored in README.md under "Who can call it".
 *
 * This module imports nothing: it is bundled into the browser, so it must stay free of `node:`.
 */

/** The roles a person can hold on a tenant — Rathvan ID's closed `app_role` set, lowest first. */
export const ROLES = ["VIEWER", "MEMBER", "ADMIN", "SUPER_ADMIN"] as const;
export type Role = (typeof ROLES)[number];

export interface RoleRow {
  /** An HTTP method, or `*` for every method. */
  method: string;
  /** A path, or a prefix ending in `/`. */
  prefix: string;
  /** The lowest role admitted. */
  min: Role;
}

export const PERSON_MINIMUM_ROLE: ReadonlyArray<RoleRow> = [
  // capability data — MEMBER
  { method: "POST", prefix: "/v1/observability/signals", min: "MEMBER" },
  { method: "POST", prefix: "/v1/logging/lines", min: "MEMBER" },
  { method: "POST", prefix: "/v1/monitoring/checks", min: "MEMBER" },
  { method: "POST", prefix: "/v1/alerting/alerts", min: "MEMBER" },
  { method: "POST", prefix: "/v1/test-suites/runs", min: "MEMBER" },
  { method: "POST", prefix: "/v1/model-router/complete", min: "MEMBER" },
  // configuration — ADMIN
  { method: "POST", prefix: "/v1/service-targets", min: "ADMIN" },
  { method: "POST", prefix: "/v1/alerting/routes", min: "ADMIN" },
  { method: "POST", prefix: "/v1/api-gateway/routes", min: "ADMIN" },
  { method: "POST", prefix: "/v1/traffic/splits", min: "ADMIN" },
  { method: "POST", prefix: "/v1/api-keys", min: "ADMIN" },
  { method: "POST", prefix: "/v1/model-router/keys", min: "ADMIN" },
  // The one PUT: a tenant's observability backend config (token, datasource uid, URL) is an upsert,
  // one row per tenant. Configuration, so ADMIN — recording a signal stays MEMBER above.
  { method: "PUT", prefix: "/v1/observability/config", min: "ADMIN" },
  // reads — VIEWER
  { method: "GET", prefix: "/", min: "VIEWER" },
  { method: "HEAD", prefix: "/", min: "VIEWER" },
  // the fail-closed default for every other write
  { method: "*", prefix: "/", min: "ADMIN" },
];

/** Whether a table row applies to this method and path. */
export function rowMatches(row: RoleRow, method: string, path: string): boolean {
  if (row.method !== "*" && row.method !== method) return false;
  return row.prefix.endsWith("/") ? path.startsWith(row.prefix) : path === row.prefix || path.startsWith(`${row.prefix}/`);
}

/** The role a person needs for this request: the first row of PERSON_MINIMUM_ROLE that matches. */
export function minimumRole(method: string, path: string): Role {
  const row = PERSON_MINIMUM_ROLE.find((r) => rowMatches(r, method, path));
  // The table's last row matches every method and path; this is for the type checker only.
  return row?.min ?? "ADMIN";
}

/** A raw role claim → a Role. Anything that is not one of ROLES, including nothing, is VIEWER. */
export function normalizeRole(raw: string | undefined): Role {
  return (ROLES as ReadonlyArray<string>).includes(raw ?? "") ? (raw as Role) : "VIEWER";
}

/** Whether `have` is at least `need` in the VIEWER < MEMBER < ADMIN < SUPER_ADMIN order. */
export function outranks(have: Role, need: Role): boolean {
  return ROLES.indexOf(have) >= ROLES.indexOf(need);
}

/**
 * The console's one predicate: may a person with this role POST to this path? The console renders
 * a write form only when this says yes, and the line it shows otherwise names `minimumRole`'s
 * answer. Both come from the table above and nowhere else.
 */
export function mayWrite(role: Role, path: string): boolean {
  return mayCall(role, "POST", path);
}

/** The same predicate for any method — the one PUT (observability config) asks it with "PUT". */
export function mayCall(role: Role, method: string, path: string): boolean {
  return outranks(role, minimumRole(method, path));
}
